/**
 * German for the e-mail template workspace (D10).
 *
 * THE SERVER SENDS KEYS, THIS FILE SAYS THEM. D4 deliberately ships no labels:
 * placeholders, scenarios, sources and error codes arrive as stable keys and
 * the client words them. Which placeholders exist is NOT decided here — the
 * catalogue of each automation comes from the server, and a key this file has
 * no word for is shown as itself rather than hidden or renamed.
 *
 * AN UNKNOWN CODE IS SAID AS A GENERAL SENTENCE, never as the raw structure the
 * server sent. Issue codes never contain document text, but a code is still
 * not a sentence a reader should have to decode.
 */
import { REQUIREMENT_TYPE_LABELS } from "../nachweise/requirementLabels";

export const TEMPLATE_PLACEHOLDER_LABELS = {
  salutation: "Anrede",
  "tenant.first_name": "Vorname",
  "organization.name": "Organisation",
  "sender.name": "Absender",
  "reminder.response_days": "Antwortfrist (Tage)",
  // PC4: the invoice values of the payment confirmation's catalogue.
  "invoice.reference": "Rechnungsreferenz",
  "invoice.period": "Abrechnungszeitraum",
  "invoice.total": "Rechnungsbetrag",
};

export function placeholderLabel(key) {
  return TEMPLATE_PLACEHOLDER_LABELS[key] || String(key ?? "");
}

/**
 * A structural block of a body (D12a), by its node type: what it is and where
 * its content comes from. The server says which blocks a body holds; this only
 * words them. An unknown type is said as an automatic part, never as its key.
 */
const STRUCTURAL_BLOCK_LABELS = {
  reminder_requirements: {
    title: "Fehlende Unterlagen",
    description: "Diese Liste wird automatisch aus den offenen Nachweisen erstellt.",
  },
};

const STRUCTURAL_BLOCK_FALLBACK = {
  title: "Automatischer Inhalt",
  description: "Dieser Teil wird beim Versand automatisch erstellt.",
};

export function structuralBlockLabel(type) {
  return STRUCTURAL_BLOCK_LABELS[type] || STRUCTURAL_BLOCK_FALLBACK;
}

export const TEMPLATE_SOURCE_LABELS = {
  system: "Systemvorlage",
  custom: "Eigene Vorlage",
};

export function templateSourceLabel(source) {
  return TEMPLATE_SOURCE_LABELS[source] || String(source ?? "");
}

/** A history row: a `system` version is the "back to the system template" step. */
export function historySourceLabel(source) {
  if (source === "system") return "Zurück zur Systemvorlage";
  if (source === "custom") return "Eigene Vorlage";
  return String(source ?? "");
}

const SCENARIO_LABELS = {
  single_residence_registration: "Ein Nachweis: Wohnsitzanmeldung",
  single_liability_insurance: "Ein Nachweis: Privathaftpflicht",
  single_deposit_pending: "Ein Nachweis: Mietkaution (ausstehend)",
  single_deposit_not_secured: "Ein Nachweis: Mietkaution (nicht gesichert)",
  bundle_one_tenancy: "Mehrere Nachweise, ein Mietverhältnis",
  bundle_two_tenancies: "Mehrere Nachweise, zwei Mietverhältnisse",
};

/** A preview scenario, by key — or described from its own fields if unknown. */
export function scenarioLabel(scenario) {
  if (!scenario) return "";
  if (SCENARIO_LABELS[scenario.key]) return SCENARIO_LABELS[scenario.key];
  const types = Array.isArray(scenario.requirement_types) ? scenario.requirement_types : [];
  if (scenario.variant === "single" && types.length === 1) {
    return `Ein Nachweis: ${REQUIREMENT_TYPE_LABELS[types[0]] || types[0]}`;
  }
  if (scenario.variant === "bundle") {
    return scenario.tenancy_count > 1
      ? `Mehrere Nachweise, ${scenario.tenancy_count} Mietverhältnisse`
      : "Mehrere Nachweise, ein Mietverhältnis";
  }
  return String(scenario.key ?? "");
}

/** What the editor can tell before any request (`draftProblems`). */
export function draftProblemLabel(code, limits = {}) {
  switch (code) {
    case "subject_empty":
      return "Der Betreff ist leer.";
    case "subject_too_long":
      return limits.max_subject_chars
        ? `Der Betreff ist zu lang (höchstens ${limits.max_subject_chars} Zeichen).`
        : "Der Betreff ist zu lang.";
    case "body_empty":
      return "Der Inhalt ist leer.";
    case "too_many_nodes":
      return "Der Inhalt ist zu umfangreich.";
    case "too_large":
      return "Die Vorlage ist zu gross.";
    case "structural_block_missing":
      return "Ein automatischer Teil der Vorlage fehlt.";
    case "structural_block_duplicate":
      return "Ein automatischer Teil der Vorlage ist mehrfach enthalten.";
    default:
      return "Die Vorlage ist so nicht gültig.";
  }
}

const ISSUE_LABELS = {
  unknown_variable: "Ein Platzhalter ist für diese Automatisierung nicht erlaubt.",
  forbidden_variable: "Ein Platzhalter ist nicht erlaubt.",
  invalid_variable: "Ein Platzhalter ist ungültig.",
  invalid_variable_key: "Ein Platzhalter ist ungültig.",
  empty_document: "Dieser Teil ist leer.",
  subject_too_long: "Der Betreff ist zu lang.",
  single_paragraph_required: "Der Betreff muss eine einzelne Zeile sein.",
  line_break_not_allowed: "Der Betreff darf keinen Zeilenumbruch enthalten.",
  marks_not_allowed: "Der Betreff darf nicht formatiert sein.",
  block_not_allowed_here: "Der Betreff darf keine Liste enthalten.",
  control_character: "Der Text enthält ein nicht erlaubtes Steuerzeichen.",
  too_many_nodes: "Der Inhalt ist zu umfangreich.",
  too_large: "Die Vorlage ist zu gross.",
  structural_block_missing: "Ein automatischer Teil der Vorlage fehlt.",
  structural_block_duplicate: "Ein automatischer Teil der Vorlage ist mehrfach enthalten.",
  structural_block_not_allowed: "Dieser automatische Teil ist hier nicht erlaubt.",
};

const FIELD_LABELS = { subject_doc: "Betreff", body_doc: "Inhalt" };

/** A `template_invalid` answer, as sentences: one per distinct problem. */
export function templateIssueLines(error) {
  const issues = Array.isArray(error?.issues) ? error.issues : [];
  const field = FIELD_LABELS[error?.field];
  const lines = [];
  for (const issue of issues) {
    const sentence = ISSUE_LABELS[issue?.code] || "Ein Teil der Vorlage ist ungültig.";
    const line = field ? `${field}: ${sentence}` : sentence;
    if (!lines.includes(line)) lines.push(line);
  }
  return lines;
}

/** A render warning of the preview (`empty_value`: a placeholder printed nothing). */
export function previewWarningLabel(warning) {
  if (warning?.code === "empty_value") {
    return `Der Platzhalter „${placeholderLabel(warning.key)}“ ist in diesem Beispiel leer.`;
  }
  return "Die Vorschau enthält einen Hinweis.";
}
